import { authenticatedFetch } from '../authenticatedFetch';

/**
 * The largest page the Bastion list routes serve. A request above this
 * answers 422, so a read that wants more rows reads consecutive pages.
 * `list_contract.py` holds the server side of this number.
 */
export const LIST_PAGE_LIMIT = 500;

/** The envelope every Bastion list route returns. */
export interface ListEnvelope<T> {
  items: T[];
  total: number;
}

/**
 * Read up to `limit` rows of one list route, one page per request.
 *
 * The first page reports `total`, which decides how many more pages the read
 * needs; those load in parallel. A read that spans pages spans requests, and
 * the server holds one transaction per request, so the assembled list is not
 * one snapshot: a row written between two requests can repeat an entry or
 * leave one out. A read of one page does not have this, so keep a view's
 * common path at or under `LIST_PAGE_LIMIT`.
 *
 * `pathFor` must put the given `limit` and `offset` into the path it builds.
 */
export async function fetchListPages<T>(
  pathFor: (limit: number, offset: number) => string,
  options: { limit: number; offset?: number; signal?: AbortSignal },
): Promise<ListEnvelope<T>> {
  const offset = Math.max(options.offset ?? 0, 0);
  const { signal } = options;

  const firstLimit = Math.min(LIST_PAGE_LIMIT, options.limit);
  const first = await authenticatedFetch<ListEnvelope<T>>(pathFor(firstLimit, offset), {
    signal,
  });
  const items = first.items.slice(0, options.limit);
  // A short first page means the rows ran out, whatever the total says. The
  // total bounds the rest of the read, so no request asks past the end.
  const remaining = Math.min(
    options.limit - items.length,
    Math.max(first.total - offset - items.length, 0),
  );
  if (remaining <= 0 || first.items.length < firstLimit) {
    return { items, total: first.total };
  }

  const start = offset + items.length;
  const pageOffsets: number[] = [];
  for (let next = start; next < start + remaining; next += LIST_PAGE_LIMIT) {
    pageOffsets.push(next);
  }
  const pages = await Promise.all(
    pageOffsets.map((pageOffset) =>
      authenticatedFetch<ListEnvelope<T>>(
        pathFor(Math.min(LIST_PAGE_LIMIT, start + remaining - pageOffset), pageOffset),
        { signal },
      ),
    ),
  );
  for (const page of pages) {
    items.push(...page.items);
  }
  return { items: items.slice(0, options.limit), total: first.total };
}
